import { setProductConfig } from '../../../actions/v3Order';

export const getTotalPages = (shoot, additionalData) => {
  let skipPoseSelection = false;

  if (shoot.isPrepay) {
    skipPoseSelection = true;
  }

  if (additionalData?.isGeskus) {
    skipPoseSelection = true;
  }

  const skipBackgroundSelection = !shoot.isGreenScreen;
  const pages = ['product'];
  if (!skipPoseSelection) pages.push('pose');
  if (!skipBackgroundSelection) pages.push('background');
  return pages;
};

export const getNextUrl = (
  current,
  pages,
  history,
  productConfig,
  event,
  itemBeingAdded = null,
  company = null,
) => {
  const currentUrlIndex = pages.indexOf(current);
  const url = history.location.pathname.split('/');
  let nextUrl = pages[currentUrlIndex + 1];
  let index = parseInt(url[url.length - 1]);

  const currentProduct = productConfig[index]?.product;
  let skipsPersonalization =
    currentProduct?.personalization_status === 'no_personalization' ||
    company?.name === 'strawbridge';

  // in this case, personalization will be prompted before checkout only
  if (
    event.isPrepay &&
    event.prepayPersonalizationOfferType ===
      'prepay_personalization_prompted_before_checkout_only'
  ) {
    skipsPersonalization = true;
  }

  // Special handling for product -> productOptions transition
  if (current === 'product' && itemBeingAdded) {
    const hasOptions =
      itemBeingAdded.product_option_sets?.length > 0 ||
      itemBeingAdded.product_options?.length > 0;

    if (hasOptions) {
      nextUrl = 'productOptions';
      const nextHash = '';
      url.splice(url.length - 2, 2);
      // For You-Pick products, always use index 0
      const optionsIndex = productConfig[0]?.products ? 0 : index;
      const result = `${url.join('/')}/${nextUrl}/${optionsIndex}${nextHash}`;
      return result;
    }
  }

  // If we're on productOptions, continue to the next page in the normal flow
  if (current === 'productOptions') {
    // Find what would have been the next page after 'product'
    const productIndex = pages.indexOf('product');
    nextUrl = pages[productIndex + 1] || pages[0];

    // Increment index if we're going back to product (next product in sequence)
    if (nextUrl === 'product' || nextUrl === pages[0]) {
      index += 1;
    }
    const nextHash = '';
    url.splice(url.length - 2, 2);
    const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
    return result;
  }

  // If we're on cropOptions, continue to personalization or wrap to product
  if (current === 'cropOptions') {
    nextUrl =
      !skipsPersonalization && !productConfig[index]?.personalization
        ? 'personalization'
        : pages[0];
    if (nextUrl !== 'personalization') index += 1;
    const nextHash = nextUrl === 'product' ? '#slots' : '';
    url.splice(url.length - 2, 2);
    const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
    return result;
  }

  // Skip pose step when the product for this slot requires no poses
  if (nextUrl === 'pose' && itemBeingAdded?.poses_count === 0) {
    const poseIndex = pages.indexOf('pose');
    nextUrl = pages[poseIndex + 1] || pages[0];
    if (nextUrl === pages[0]) index += 1;
  }

  if (currentUrlIndex === pages.length - 1) {
    // Check if crop options should be shown for this child product
    // Use itemBeingAdded for 'product' step since productConfig may not be updated yet
    const productToCheck =
      current === 'product' ? itemBeingAdded : currentProduct;
    const shouldShowCropOptions =
      event?.isPrepay &&
      event?.crop_options_enabled &&
      event?.crop_options?.length > 0 &&
      productToCheck?.poses_count > 0 &&
      productToCheck?.crop_options_enabled !== false &&
      !productConfig[index]?.cropOptions;

    if (shouldShowCropOptions) {
      nextUrl = 'cropOptions';
    } else {
      nextUrl =
        !skipsPersonalization && !productConfig[index]?.personalization
          ? 'personalization'
          : pages[0];
      if (nextUrl !== 'personalization') index += 1;
    }
  }

  if (currentUrlIndex < 0) index += 1;

  const nextHash = nextUrl === 'product' ? '#slots' : '';
  url.splice(url.length - 2, 2);

  const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
  return result;
};

const updateStackRouter = (dispatch, history) => {
  const { pathname, hash } = history.location;
  const paths = [pathname + hash];
  if (!hash && pathname.includes('/product/'))
    paths.unshift(pathname + '#slots');
  dispatch(addToConfigStack(paths));
};

/**
 * Get the array of poses for a slot from productConfig (supports both .pose and .poses).
 */
export const getPosesForSlot = (config) => {
  if (!config) return [];
  if (Array.isArray(config.poses)) return config.poses;
  return config.pose ? [config.pose] : [];
};

/**
 * Get required pose count for a slot: 0 if pose step is skipped globally, else product.poses_count ?? 1.
 */
export const getRequiredPoseCountForSlot = (config, pages) => {
  if (!pages.includes('pose')) return 0;
  const product = config?.product;
  if (!product) return 0;
  const count = product.poses_count;
  return typeof count === 'number' ? count : 1;
};

export const addToConfig = ({
  dispatch,
  history,
  productConfig,
  pages,
  itemToAdd,
  event,
  cropIndex = null,
  company = null,
}) => {
  const { pathname, hash } = history.location;
  const currentUrlSplit = pathname.split('/');
  const current = currentUrlSplit[currentUrlSplit.length - 2];
  const index = parseInt(pathname.split('/').pop().replace('#slots', ''), 10);
  let newItem = itemToAdd;

  if (cropIndex !== null && current === 'pose') {
    newItem = { ...itemToAdd };
    newItem.selected_crop = newItem.cropped_images[cropIndex];
  }

  let item = { [current]: newItem };

  if (pathname.includes('product/') && hash !== '#slots')
    item.usedSlotSelectionUi = true;

  // If we're on cropOptions page, merge the selections with the existing product config
  if (current === 'cropOptions') {
    const existingConfig = productConfig[index] || {};
    const mergedItem = { ...existingConfig, cropOptions: newItem };
    dispatch(setProductConfig({ index, itemToAdd: mergedItem }));
    history.push(
      getNextUrl(current, pages, history, productConfig, event, null, company),
    );
    return;
  }

  // If we're on productOptions page, merge the options with the existing product config
  if (current === 'productOptions') {
    const existingConfig = productConfig[index] || {};

    // For You-Pick products, we need to update the specific product in the products array
    if (existingConfig.products?.length > 0) {
      // Find the last product (the one we just configured)
      const updatedProducts = [...existingConfig.products];
      const lastProductIndex = updatedProducts.length - 1;
      updatedProducts[lastProductIndex] = {
        ...updatedProducts[lastProductIndex],
        productOptions: newItem,
      };

      const mergedItem = {
        ...existingConfig,
        products: updatedProducts,
      };
      dispatch(setProductConfig({ index, itemToAdd: mergedItem }));
    } else {
      // Regular flow: just add productOptions to the config
      const mergedItem = { ...existingConfig, productOptions: newItem };
      dispatch(setProductConfig({ index, itemToAdd: mergedItem }));
    }
    history.push(
      getNextUrl(
        current,
        pages,
        history,
        productConfig,
        event,
        current === 'product' ? newItem : null,
        company,
      ),
    );
    return;
  }

  // Pose step: support multiple poses per product (poses_count === 2) and skip when poses_count === 0
  if (current === 'pose') {
    const existingConfig = productConfig[index] || {};
    const product = existingConfig.product;
    const posesCount =
      typeof product?.poses_count === 'number' ? product.poses_count : 1;

    if (posesCount === 0) {
      // Product requires no pose; advance without storing
      history.push(
        getNextUrl(
          current,
          pages,
          history,
          productConfig,
          event,
          null,
          company,
        ),
      );
      return;
    }

    if (posesCount >= 2) {
      const existingPoses = getPosesForSlot(existingConfig);
      const newPoses = [...existingPoses, newItem].slice(0, posesCount);
      item = { poses: newPoses };
      dispatch(setProductConfig({ index, itemToAdd: item }));
      // Only advance to next step when we have selected enough poses for this slot
      if (newPoses.length >= posesCount) {
        history.push(
          getNextUrl(
            current,
            pages,
            history,
            {
              ...productConfig,
              [index]: { ...existingConfig, poses: newPoses },
            },
            event,
            null,
            company,
          ),
        );
      }
      return;
    }
  }

  dispatch(setProductConfig({ index, itemToAdd: item }));
  history.push(
    getNextUrl(
      current,
      pages,
      history,
      productConfig,
      event,
      current === 'product' ? newItem : null,
      company,
    ),
  );
};
